import { BarChartOutlined, DollarOutlined, EyeFilled, GroupOutlined } from '@ant-design/icons';
import { useTheme } from '@mui/material/styles';
import { Badge } from '@trm/ui-kit';
import { FormattedMessage } from 'react-intl';
import ActivityIcon from '~/assets/images/activitylog.svg';
import AdministrationIcon from '~/assets/images/administration.svg';
import PromosIcon from '~/assets/images/promos.svg';
import UsersIcon from '~/assets/images/users.svg';
import { TRAINING_CAMP_NAV_URL } from '~/pages/dashboard/components/NewTraderDashboard/constants';
import { NavItemType } from '~/types/menu';
import { PagePolicies } from '~/utils/roles';

const DashboardIcon = ({ style }: { style?: React.CSSProperties }) => {
  const color = style?.color || 'currentColor';
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <path
        d="M6 12.75H12M8.26327 2.07306L3.17654 6.0294C2.83652 6.29387 2.6665 6.4261 2.54402 6.5917C2.43552 6.73839 2.3547 6.90365 2.30552 7.07935C2.25 7.2777 2.25 7.49308 2.25 7.92385V13.35C2.25 14.1901 2.25 14.6102 2.41349 14.931C2.5573 15.2133 2.78677 15.4427 3.06901 15.5866C3.38988 15.75 3.80992 15.75 4.65 15.75H13.35C14.1901 15.75 14.6101 15.75 14.931 15.5866C15.2132 15.4427 15.4427 15.2133 15.5865 14.931C15.75 14.6102 15.75 14.1901 15.75 13.35V7.92385C15.75 7.49308 15.75 7.2777 15.6945 7.07935C15.6453 6.90365 15.5645 6.73839 15.456 6.5917C15.3335 6.4261 15.1635 6.29387 14.8235 6.02941L9.73673 2.07306C9.47324 1.86812 9.34149 1.76565 9.19601 1.72626C9.06765 1.69151 8.93235 1.69151 8.80399 1.72626C8.65851 1.76565 8.52677 1.86812 8.26327 2.07306Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const TrainingCampIcon = ({ style }: { style?: React.CSSProperties }) => {
  const color = style?.color || 'currentColor';
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <path
        d="M3.33496 6.20251V11.2118C3.33496 11.5109 3.33496 11.6605 3.38051 11.7925C3.42078 11.9093 3.48652 12.0157 3.57295 12.1039C3.67068 12.2037 3.80446 12.2706 4.07201 12.4044L8.57201 14.6544C8.79064 14.7637 8.89997 14.8183 9.01463 14.8399C9.11619 14.8589 9.2204 14.8589 9.32196 14.8399C9.43662 14.8183 9.54595 14.7637 9.76458 14.6544L14.2646 12.4044C14.5321 12.2706 14.6659 12.2037 14.7636 12.1039C14.8501 12.0157 14.9158 11.9093 14.9561 11.7925C15.0016 11.6605 15.0016 11.5109 15.0016 11.2118V6.20251M0.834961 4.95251L8.87015 0.934912C8.97947 0.880253 9.03413 0.852923 9.09146 0.842167C9.14224 0.83264 9.19435 0.83264 9.24513 0.842167C9.30246 0.852923 9.35712 0.880253 9.46644 0.934912L17.5016 4.95251L9.46644 8.9701C9.35712 9.02476 9.30246 9.05209 9.24513 9.06285C9.19435 9.07238 9.14224 9.07238 9.09146 9.06285C9.03413 9.05209 8.97947 9.02476 8.87015 8.9701L0.834961 4.95251Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const AccountsIcon = ({ style }: { style?: React.CSSProperties }) => {
  const color = style?.color || 'currentColor';
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <path
        d="M15.75 15.75H4.65C3.80992 15.75 3.38988 15.75 3.06901 15.5865C2.78677 15.4427 2.5573 15.2132 2.41349 14.931C2.25 14.6101 2.25 14.1901 2.25 13.35V2.25M5.25 10.875V13.125M8.625 8.625V13.125M12 6.375V13.125M15.375 4.125V13.125"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const BillingIcon = ({ style }: { style?: React.CSSProperties }) => {
  const color = style?.color || 'currentColor';
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <path
        d="M16.5 7.5H1.5M8.25 10.5H4.5M1.5 6.15L1.5 11.85C1.5 12.6901 1.5 13.1101 1.66349 13.431C1.8073 13.7132 2.03677 13.9427 2.31901 14.0865C2.63988 14.25 3.05992 14.25 3.9 14.25L14.1 14.25C14.9401 14.25 15.3601 14.25 15.681 14.0865C15.9632 13.9427 16.1927 13.7132 16.3365 13.431C16.5 13.1101 16.5 12.6901 16.5 11.85V6.15C16.5 5.30992 16.5 4.88988 16.3365 4.56902C16.1927 4.28677 15.9632 4.0573 15.681 3.91349C15.3601 3.75 14.9401 3.75 14.1 3.75L3.9 3.75C3.05992 3.75 2.63988 3.75 2.31901 3.91349C2.03677 4.0573 1.8073 4.28677 1.66349 4.56901C1.5 4.88988 1.5 5.30992 1.5 6.15Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const CertificatesIcon = ({ style }: { style?: React.CSSProperties }) => {
  const color = style?.color || 'currentColor';
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <g clipPath="url(#clip0_5785_4612)">
        <path
          d="M9 11.25C6.51472 11.25 4.5 9.23528 4.5 6.75V2.58333C4.5 2.27295 4.5 2.11775 4.54523 1.99348C4.62105 1.78516 4.78516 1.62105 4.99348 1.54523C5.11775 1.5 5.27295 1.5 5.58333 1.5H12.4167C12.7271 1.5 12.8822 1.5 13.0065 1.54523C13.2148 1.62105 13.3789 1.78516 13.4548 1.99348C13.5 2.11775 13.5 2.27295 13.5 2.58333V6.75C13.5 9.23528 11.4853 11.25 9 11.25ZM9 11.25V13.5M13.5 3H15.375C15.7245 3 15.8992 3 16.037 3.05709C16.2208 3.13321 16.3668 3.27922 16.4429 3.46299C16.5 3.60082 16.5 3.77554 16.5 4.125V4.5C16.5 5.19748 16.5 5.54622 16.4233 5.83234C16.2153 6.6088 15.6088 7.21528 14.8323 7.42333C14.5462 7.5 14.1975 7.5 13.5 7.5M4.5 3H2.625C2.27554 3 2.10082 3 1.96299 3.05709C1.77922 3.13321 1.63321 3.27922 1.55709 3.46299C1.5 3.60082 1.5 3.77554 1.5 4.125V4.5C1.5 5.19748 1.5 5.54622 1.57667 5.83234C1.78472 6.6088 2.3912 7.21528 3.16766 7.42333C3.45378 7.5 3.80252 7.5 4.5 7.5M5.58333 16.5H12.4167C12.6008 16.5 12.75 16.3508 12.75 16.1667C12.75 14.6939 11.5561 13.5 10.0833 13.5H7.91667C6.44391 13.5 5.25 14.6939 5.25 16.1667C5.25 16.3508 5.39924 16.5 5.58333 16.5Z"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_5785_4612">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const PayoutsIcon = ({ style }: { style?: React.CSSProperties }) => {
  const color = style?.color || 'currentColor';
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <path
        d="M4.5 12C4.5 13.6569 5.84315 15 7.5 15H10.5C12.1569 15 13.5 13.6569 13.5 12C13.5 10.3431 12.1569 9 10.5 9H7.5C5.84315 9 4.5 7.65685 4.5 6C4.5 4.34315 5.84315 3 7.5 3H10.5C12.1569 3 13.5 4.34315 13.5 6M9 1.5V16.5"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const ProfileIcon = ({ style }: { style?: React.CSSProperties }) => {
  const color = style?.color || 'currentColor';
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <g clipPath="url(#clip0_5785_4584)">
        <path
          d="M3.98722 14.5788C4.44347 13.5039 5.50869 12.75 6.75 12.75H11.25C12.4913 12.75 13.5565 13.5039 14.0128 14.5788M12 7.125C12 8.78185 10.6569 10.125 9 10.125C7.34315 10.125 6 8.78185 6 7.125C6 5.46815 7.34315 4.125 9 4.125C10.6569 4.125 12 5.46815 12 7.125ZM16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_5785_4584">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const HelpIcon = ({ style }: { style?: React.CSSProperties }) => {
  const color = style?.color || 'currentColor';
  return (
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={style}
    >
      <g clipPath="url(#clip0_5785_4619)">
        <path
          d="M6.8175 6.75C6.99383 6.24875 7.34186 5.82608 7.79997 5.55685C8.25807 5.28762 8.79667 5.1892 9.32038 5.27903C9.84409 5.36886 10.3191 5.64114 10.6613 6.04765C11.0035 6.45415 11.1908 6.96864 11.19 7.5C11.19 9 8.94 9.75 8.94 9.75M9 12.75H9.0075M16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_5785_4619">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const UsersIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={UsersIcon}
      alt="users"
      style={{ filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none' }}
    />
  );
};

const PromosIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={PromosIcon}
      alt="promos"
      style={{ filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none' }}
    />
  );
};

const ActivityIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={ActivityIcon}
      alt="activity"
      style={{ filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none' }}
    />
  );
};

const AdministrationIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={AdministrationIcon}
      alt="administration"
      style={{ filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none' }}
    />
  );
};

// icons
const icons = {
  BarChartOutlined,
  DollarOutlined,
  EyeFilled,
  GroupOutlined,
  DashboardIcon,
  TrainingCampIcon,
  AccountsIcon,
  BillingIcon,
  CertificatesIcon,
  PayoutsIcon,
  ProfileIcon,
  HelpIcon,
  UsersIcon: UsersIconComponent,
  PromosIcon: PromosIconComponent,
  ActivityIcon: ActivityIconComponent,
  AdministrationIcon: AdministrationIconComponent,
};

// ==============================|| MENU ITEMS ||============================== //

const menu: NavItemType = {
  id: 'dashboard',
  type: 'group',
  children: [
    {
      id: 'dashboard',
      title: (
        <strong>
          <FormattedMessage id="Dashboard" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/default',
      policies: PagePolicies.EndUserProfilePage,
      icon: icons.DashboardIcon,
    },
    {
      id: 'training-camp',
      title: (
        <strong>
          <FormattedMessage id="Training Camp" />
        </strong>
      ),
      type: 'item',
      url: TRAINING_CAMP_NAV_URL,
      external: true,
      target: true,
      policies: PagePolicies.EndUserProfilePage,
      icon: icons.TrainingCampIcon,
      badge: (
        <Badge type="pill-color" color="brand" size="sm">
          <FormattedMessage id="New" />
        </Badge>
      ),
    },
    {
      id: 'profiledetails',
      title: (
        <strong>
          <FormattedMessage id="Profile" />
        </strong>
      ),
      type: 'item',
      icon: icons.ProfileIcon,
      policies: PagePolicies.EndUserProfilePage,
      url: '/dashboard/profile/details',
    },
    {
      id: 'billing',
      title: (
        <strong>
          <FormattedMessage id="Billing" />
        </strong>
      ),
      icon: icons.BillingIcon,
      type: 'item',
      policies: PagePolicies.EndUserProfilePage,
      url: '/dashboard/profile/billing',
    },
    {
      id: 'accounts',
      title: (
        <strong>
          <FormattedMessage id="Accounts" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/accounts',
      policies: PagePolicies.EndUserAccountsPage,
      icon: icons.AccountsIcon,
    },
    {
      id: 'payouts',
      title: (
        <strong>
          <FormattedMessage id="Payouts" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/payouts',
      policies: PagePolicies.EndUserPayoutsPage,
      icon: icons.PayoutsIcon,
    },
    {
      id: 'Achievements',
      title: (
        <strong>
          <FormattedMessage id="Achievements" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/certificates',
      policies: PagePolicies.EndUserCertificatesPage,
      icon: icons.CertificatesIcon,
    },
    {
      id: 'adminusers',
      title: (
        <strong>
          <FormattedMessage id="Users" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/users',
      policies: PagePolicies.AdminUsersPage,
      icon: icons.UsersIcon,
    },
    {
      id: 'adminaccounts',
      title: (
        <strong>
          <FormattedMessage id="Accounts" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/accounts',
      policies: PagePolicies.AdminAccountsPage,
      icon: icons.AccountsIcon,
    },
    {
      id: 'admincoupons',
      title: (
        <strong>
          <FormattedMessage id="Promos" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/coupons',
      policies: PagePolicies.AdminCouponsPage,
      icon: icons.PromosIcon,
    },
    {
      id: 'adminpayouts',
      title: (
        <strong>
          <FormattedMessage id="Payouts" />
        </strong>
      ),
      type: 'collapse',
      policies: PagePolicies.AdminPayoutsPage,
      icon: icons.PayoutsIcon,
      children: [
        {
          id: 'admintracker',
          title: (
            <strong>
              <FormattedMessage id="Tracker" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/payouts',
          policies: PagePolicies.AdminPayoutBatchesPage,
        },
        {
          id: 'adminpayoutbatch',
          title: (
            <strong>
              <FormattedMessage id="Payout Batches" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/payouts/batches',
          policies: PagePolicies.AdminPayoutBatchesPage,
        },
      ],
    },
    {
      id: 'adminkyc',
      title: (
        <strong>
          <FormattedMessage id="KYC" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/kyc',
      policies: PagePolicies.AdminKYCPage,
      icon: icons.EyeFilled,
    },
    {
      id: 'adminfinance',
      title: (
        <strong>
          <FormattedMessage id="Finance" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/finance',
      policies: PagePolicies.AdminFinancePage,
      icon: icons.BarChartOutlined,
    },
    {
      id: 'adminTransactions',
      title: (
        <strong>
          <FormattedMessage id="Transactions" />
        </strong>
      ),
      type: 'collapse',
      policies: PagePolicies.AdminTransactionsPage,
      icon: icons.DollarOutlined,
      children: [
        {
          id: 'nmiTransactions',
          title: (
            <strong>
              <FormattedMessage id="NMI" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/purchases',
          policies: PagePolicies.AdminTransactionsPage,
        },
        {
          id: 'nuveiTransactions',
          title: (
            <strong>
              <FormattedMessage id="Others" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/transactions',
          policies: PagePolicies.AdminTransactionsPage,
        },
      ],
    },
    {
      id: 'adminlog',
      title: (
        <strong>
          <FormattedMessage id="Activity Log" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/log',
      policies: PagePolicies.AdminLogPage,
      icon: icons.ActivityIcon,
    },
    {
      id: 'adminorganization',
      title: (
        <strong>
          <FormattedMessage id="Administration" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/organization',
      policies: PagePolicies.AdminOrganizationPage,
      icon: icons.AdministrationIcon,
    },
    {
      id: 'adminimpactgroups',
      title: (
        <strong>
          <FormattedMessage id="Impact Groups" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/impact-groups',
      policies: PagePolicies.AdminImpactGroupsPage,
      icon: icons.GroupOutlined,
    },
    {
      id: 'helpcenter',
      title: (
        <strong>
          <FormattedMessage id="Help Center" />
        </strong>
      ),
      type: 'item',
      url: 'https://help.topstep.com/en',
      external: true,
      target: true,
      icon: icons.HelpIcon,
    },
  ],
};

export default menu;
